import { useEffect, useState, useCallback } from "react";
import { ArrowLeft, MapPin, Phone, Mail, User2, MessageSquare, Loader2, Clock, History, Navigation, ShoppingCart, Lock, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { useEmployeeApi } from "../employeeApi";
import { StatusBadge, PriorityBadge } from "../employeeUi";
import WorkflowSection from "./WorkflowSection";
import JobMaterials from "./JobRegistration";
import InvoiceDraftCard from "./InvoiceDraftCard";
import JobNotes from "./JobNotes";
import JobFiles from "./JobFiles";
import PortalLinkCard from "./PortalLinkCard";

const mapsUrl = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

export default function JobDetailScreen({ jobId, onBack }) {
    const api = useEmployeeApi();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [invoiceKey, setInvoiceKey] = useState(0);
    const [invoiceLocked, setInvoiceLocked] = useState(false);

    const reload = useCallback(() => api.getJob(jobId).then(setJob), [api, jobId]);

    useEffect(() => {
        api.getInvoiceDraft(jobId).then((d) => setInvoiceLocked(!!d.locked)).catch(() => setInvoiceLocked(false));
    }, [api, jobId, invoiceKey]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.getJob(jobId)
            .then((d) => { if (alive) setJob(d); })
            .catch((e) => {
                const code = e?.response?.status;
                toast.error(code === 403 ? "Du har ikke adgang til denne opgave" : "Kunne ikke hente opgaven");
                onBack();
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [api, jobId, onBack]);

    if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
    if (!job) return null;

    const c = job.customer || {};
    const timeline = job.timeline || [];
    const finished = job.workflow?.stage === "faerdig";

    return (
        <div className="space-y-4" data-testid="employee-job-detail">
            <button onClick={onBack} data-testid="job-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
                <ArrowLeft className="w-4 h-4" /> Opgaver
            </button>

            <div>
                <div className="flex items-center gap-2 mb-2"><StatusBadge status={job.status} /><PriorityBadge priority={job.priority} /></div>
                <h1 className="text-xl font-bold text-slate-900 leading-snug" data-testid="job-title">{job.task}</h1>
                {(job.date || job.time) && <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><Clock className="w-4 h-4" />{[job.date, job.time].filter(Boolean).join(" · ")}</p>}
            </div>

            {job.purchase && <EmployeePurchaseBlock p={job.purchase} />}

            {job.workflow && (
                <WorkflowSection jobId={jobId} job={job} wf={job.workflow} locked={!!job.locked} onRefresh={() => reload().catch(() => {})}
                    onChange={(wf, status) => { setJob((j) => ({ ...j, workflow: wf, ...(status ? { status } : {}) })); if (wf?.stage === "faerdig") { setInvoiceKey((k) => k + 1); reload().catch(() => {}); } }} />
            )}

            <section className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid="job-customer">
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Kunde</h2>
                <p className="font-semibold text-slate-900 flex items-center gap-2"><User2 className="w-4 h-4 text-slate-400" />{c.name || "Ukendt kunde"}</p>
                <div className="mt-2 space-y-1.5 text-sm">
                    {c.address && <p className="flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4 text-slate-400 shrink-0" />{c.address}</p>}
                    {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-blue-600"><Phone className="w-4 h-4 shrink-0" />{c.phone}</a>}
                    {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-blue-600 break-all"><Mail className="w-4 h-4 shrink-0" />{c.email}</a>}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                    {c.address && <a href={mapsUrl(c.address)} target="_blank" rel="noreferrer" data-testid="customer-maps" className="h-10 rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center justify-center gap-1.5 active:scale-[0.99] transition-transform"><Navigation className="w-4 h-4" />Åbn i Maps</a>}
                    {c.phone && <a href={`tel:${c.phone}`} data-testid="customer-call" className="h-10 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold flex items-center justify-center gap-1.5"><Phone className="w-4 h-4" />Ring</a>}
                </div>
            </section>

            {(job.description || job.customer_message) && (
                <section className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid="job-info">
                    <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" />Beskrivelse</h2>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{job.description || job.customer_message}</p>
                </section>
            )}

            {finished && <InvoiceDraftCard jobId={jobId} refreshKey={invoiceKey} onChanged={() => setInvoiceKey((k) => k + 1)} />}

            <JobMaterials jobId={jobId} job={job} locked={invoiceLocked} onChanged={() => setInvoiceKey((k) => k + 1)} />

            <JobFiles jobId={jobId} />

            <JobNotes jobId={jobId} notes={job.notes || []} customerRecordNote={c.notes} onNotes={(notes) => setJob((j) => ({ ...j, notes }))} />

            <PortalLinkCard jobId={jobId} />

            {!finished && <InvoiceDraftCard jobId={jobId} refreshKey={invoiceKey} onChanged={() => setInvoiceKey((k) => k + 1)} />}

            {timeline.length > 0 && (
                <section className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid="job-timeline">
                    <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5"><History className="w-3.5 h-3.5" />Tidslinje</h2>
                    <ul className="space-y-3">
                        {timeline.map((t, i) => (
                            <li key={i} className="flex gap-3" data-testid="timeline-item">
                                <div className="flex flex-col items-center">
                                    <span className={`w-2.5 h-2.5 rounded-full mt-1 ${t.actor === "AI-telefon" ? "bg-violet-500" : t.actor === "System" ? "bg-slate-400" : "bg-blue-500"}`} />
                                    {i < timeline.length - 1 && <span className="w-px flex-1 bg-slate-200 mt-1" />}
                                </div>
                                <div className="pb-1">
                                    <p className="text-sm text-slate-800"><span className="font-semibold">{t.actor || "Medarbejder"}</span> {t.action}</p>
                                    {t.time && <p className="text-xs text-slate-400">{t.time}</p>}
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
}

const PURCHASE_STATUS = {
    needs_purchase: { t: "🔴 Ikke bestilt", c: "text-red-700" },
    ordered: { t: "🟡 Bestilt – venter på varen", c: "text-amber-700" },
    in_stock: { t: "🟢 På lager", c: "text-emerald-700" },
};

function EmployeePurchaseBlock({ p }) {
    const inStock = p.status === "in_stock";
    const st = PURCHASE_STATUS[p.status] || PURCHASE_STATUS.needs_purchase;
    const varenavn = [p.brand, p.item].filter(Boolean).join(" · ") || "Specialbestilling";
    return (
        <section data-testid="employee-purchase-block"
            className={`rounded-2xl border p-4 shadow-sm ${inStock ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                <ShoppingCart className="w-3.5 h-3.5" /> Specialbestilling
            </h2>
            <p className="font-semibold text-slate-900" data-testid="employee-purchase-item">{varenavn}</p>
            {p.manufacturer_part_number && (
                <p className="text-xs text-slate-500 mt-0.5" data-testid="employee-purchase-mpn">Producentnr.: {p.manufacturer_part_number}</p>
            )}
            {p.inventory_location && (
                <p className="text-sm text-slate-700 mt-1 flex items-center gap-1.5" data-testid="employee-purchase-location">
                    <PackageCheck className="w-4 h-4 text-emerald-600" /> Lagerplacering: <span className="font-semibold">{p.inventory_location}</span>
                </p>
            )}
            <p className={`text-sm font-semibold mt-2 ${st.c}`} data-testid="employee-purchase-status">{st.t}</p>
            {inStock ? (
                <p className="mt-1 text-sm font-bold text-emerald-700 flex items-center gap-1.5" data-testid="employee-purchase-ready">🟢 Vare klar</p>
            ) : (
                <p className="mt-1 text-xs text-amber-700 flex items-center gap-1.5" data-testid="employee-purchase-waiting">
                    <Lock className="w-3.5 h-3.5" /> Afventer vare — opgaven kan ikke tages/startes endnu.
                </p>
            )}
        </section>
    );
}